import { motion } from 'framer-motion';
import { Layers, UserCheck, Link2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  VCard Preview                                                      */
/* ------------------------------------------------------------------ */

function VCardPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="rounded-2xl border bg-card p-5 md:p-6 shadow-subtle"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Finding</p>
          <p className="text-sm md:text-base font-semibold" role="heading" aria-level={3}>
            Participants adapt coping strategies based on institutional trust
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] md:text-[11px] font-medium text-primary border border-primary/20">RQ1</span>
        <span className="px-2 py-0.5 rounded-full bg-evidence/8 text-[10px] md:text-[11px] font-medium text-evidence border border-evidence/20">3 quotes</span>
        <span className="px-2 py-0.5 rounded-full bg-evidence/8 text-[10px] md:text-[11px] font-medium text-evidence border border-evidence/20">2 sources</span>
        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] md:text-[11px] font-medium text-primary border border-primary/20">Confidence: High</span>
      </div>
      <div className="space-y-3 relative">
        <svg className="absolute -left-1 top-0 h-full w-3 pointer-events-none" aria-hidden="true">
          <line x1="6" y1="4" x2="6" y2="100%" stroke="hsl(var(--evidence) / 0.25)" strokeWidth="1.5" strokeDasharray="4 3" />
        </svg>
        <div className="rounded-lg border bg-paper p-3 ml-3">
          <p className="text-xs text-muted-foreground leading-relaxed italic">"When I felt the system was fair, I was more willing to be open about my difficulties..."</p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">Source 1 · Lines 42–44</span>
            <span className="text-[10px] font-medium text-evidence" aria-hidden="true">Open context</span>
          </div>
        </div>
        <div className="rounded-lg border bg-paper p-3 ml-3">
          <p className="text-xs text-muted-foreground leading-relaxed italic">"Trust in the process made me feel like my voice actually mattered, so I shared more."</p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">Source 2 · Lines 78–80</span>
            <span className="text-[10px] font-medium text-evidence" aria-hidden="true">Open context</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export function WhyVerithosSection() {
  return (
    <section id="why-verithos" className="py-12 sm:py-16 md:py-20 2xl:py-24 relative scroll-mt-24">
      <div className="max-w-6xl 2xl:max-w-7xl mx-auto px-5 sm:px-6 2xl:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 2xl:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-evidence/10 border border-evidence/20 mb-5">
              <span className="text-[10px] md:text-xs font-semibold text-evidence uppercase tracking-wider">Why It Matters</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-3xl 2xl:text-4xl font-semibold tracking-tight text-balance mb-6 leading-tight">
              Most tools stop at themes.{' '}
              <span className="text-primary">Verithos gets you to a draft you can actually submit.</span>
            </h2>
            <div className="space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg">
              <p>
                Every finding links to a code. Every code links to a quote.
                Every quote links to its source. So when it's time to write up, the draft practically builds itself.
              </p>
            </div>
          </motion.div>
          <div className="md:sticky md:top-28">
            <VCardPreview />
          </div>
        </div>
      </div>
    </section>
  );
}
